import {
  Button,
  Dialog,
  InfoTooltip,
  NumberField,
  SelectField,
  Switch,
} from '@wandb/components';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useState} from 'react';

import {
  OrganizationSubscriptionStatus,
  useUserOrganizationsQuery,
} from '../generated/graphql';
import {
  type PlanInfoWithActualPlans,
  type PlanWithPrivileges,
  type Privileges,
  useSubscriptionPlans,
} from '../util/accounts/pricing';
import {
  COMPUTE_HOUR_AGGRESSIVE_DAYS_THRESHOLD,
  FREE_ACADEMIC_TRIAL,
  FREE_ENTERPRISE_PLAN_TRIAL,
  FREE_PERSONAL_ACCOUNT,
  FREE_STARTER_PLAN_TRIAL,
  getAggressiveCookie,
  getComputeHourLimitCookie,
  getDaysUntilExpireCookie,
  getLimitString,
  getOrganizationIDCookie,
  getStorageGBLimitCookie,
  getSubscriptionStatusCookie,
  getSubscriptionTypeCookie,
  getTrackedGBLimitCookie,
  PAID_ENTERPRISE_PLAN,
  PAID_STARTER_TIER_1_PLAN,
  setAggressiveCookie,
  setComputeHourLimitCookie,
  setDaysUntilExpireCookie,
  setOrganizationIDCookie,
  setStorageGBLimitCookie,
  setSubscriptionStatusCookie,
  setSubscriptionTypeCookie,
  setTrackedGBLimitCookie,
  type SubscriptionType,
  unsetAllUsageLimitCookies,
} from '../util/usageLimit';

interface UsageSimulationModalProps {
  onClose: () => void;
}

const SUBSCRIPTION_TYPE_OPTIONS: {
  id: SubscriptionType;
  label: string;
}[] = [
  {id: FREE_PERSONAL_ACCOUNT, label: 'Free personal subscription'},
  {id: FREE_ACADEMIC_TRIAL, label: 'Free academic trial'},
  {id: FREE_STARTER_PLAN_TRIAL, label: 'Free starter trial'},
  {id: PAID_STARTER_TIER_1_PLAN, label: 'Starter tier 1 plan'},
  {
    id: FREE_ENTERPRISE_PLAN_TRIAL,
    label: 'Free enterprise trial',
  },
  {id: PAID_ENTERPRISE_PLAN, label: 'Enterprise plan'},
];

type LimitType = 'Storage GB' | 'Tracked GB' | 'Tracked hours';

interface LimitInputAttributes {
  privilegeLimitName: keyof Privileges;
  unit: string;
  limit: number | null;
  setLimit: (value: React.SetStateAction<number | null>) => void;
}

const UsageSimulationModal: React.FC<UsageSimulationModalProps> = React.memo(
  ({onClose}) => {
    const subscriptionTypeInCookie = getSubscriptionTypeCookie();
    const initialOrgID = getOrganizationIDCookie();
    const [orgID, setOrgID] = useState<string | undefined>(initialOrgID);
    const initialSubscriptionType: SubscriptionType =
      subscriptionTypeInCookie || FREE_PERSONAL_ACCOUNT;

    const [subscriptionType, setSubscriptionType] = useState<SubscriptionType>(
      initialSubscriptionType
    );
    const [storageGBLimit, setStorageGBLimit] = useState(
      getStorageGBLimitCookie()
    );
    const [trackedGBLimit, setTrackedGBLimit] = useState(
      getTrackedGBLimitCookie()
    );
    const [computeHourLimit, setComputeHourLimit] = useState(
      getComputeHourLimitCookie()
    );
    const [daysUntilExpire, setDaysUntilExpire] = useState(
      getDaysUntilExpireCookie()
    );
    const [aggressive, setAggressive] = useState(getAggressiveCookie());
    const [subscriptionStatus, setSubscriptionStatus] = useState(
      getSubscriptionStatusCookie()
    );

    const {planInfo} = useSubscriptionPlans();

    const userOrgs = useUserOrganizationsQuery();
    const orgOptions =
      userOrgs.data?.viewer?.organizations.map(org => {
        return {id: org.id, label: org.name};
      }) ?? [];

    const onSubmit = useCallback(() => {
      setOrganizationIDCookie(orgID ?? '');
      setSubscriptionTypeCookie(subscriptionType);
      setStorageGBLimitCookie(storageGBLimit);
      setTrackedGBLimitCookie(trackedGBLimit);
      setComputeHourLimitCookie(computeHourLimit);
      setDaysUntilExpireCookie(daysUntilExpire);
      setAggressiveCookie(aggressive);
      setSubscriptionStatusCookie(subscriptionStatus);
      onClose();
      window.location.reload();
    }, [
      onClose,
      orgID,
      subscriptionType,
      storageGBLimit,
      trackedGBLimit,
      computeHourLimit,
      daysUntilExpire,
      aggressive,
      subscriptionStatus,
    ]);

    const onReset = useCallback(() => {
      unsetAllUsageLimitCookies();
      window.location.reload();
    }, []);

    const getLimitInputAttributes = useCallback(
      (type: LimitType): LimitInputAttributes | null => {
        switch (type) {
          case 'Storage GB':
            return {
              privilegeLimitName: 'storageLimitGB',
              unit: 'GB',
              limit: storageGBLimit,
              setLimit: setStorageGBLimit,
            };
          case 'Tracked GB':
            return {
              privilegeLimitName: 'referenceLimitGB',
              unit: 'GB',
              limit: trackedGBLimit,
              setLimit: setTrackedGBLimit,
            };
          case 'Tracked hours':
            return {
              privilegeLimitName: 'computeHours',
              unit: 'hrs',
              limit: computeHourLimit,
              setLimit: setComputeHourLimit,
            };
          default:
            return null;
        }
      },
      [storageGBLimit, trackedGBLimit, computeHourLimit]
    );

    return (
      <Dialog.Modal
        isOpen
        onOpenChange={open => {
          if (!open) {
            onClose();
          }
        }}>
        <Dialog.Heading>Simulate Usage for Testing Banners</Dialog.Heading>
        <Dialog.Content>
          <TailwindContents>
            <div className="flex items-baseline justify-between pb-16">
              <h3>Subscription type</h3>
              <SelectField
                aria-label="Subscription type"
                items={SUBSCRIPTION_TYPE_OPTIONS}
                value={subscriptionType}
                onChange={key => {
                  if (key == null) {
                    return;
                  }
                  const option = SUBSCRIPTION_TYPE_OPTIONS.find(
                    o => o.id === key
                  );
                  if (option) {
                    setSubscriptionType(option.id);
                  }
                }}
              />
            </div>
            <div className="flex items-baseline justify-between pb-16">
              <div className="flex items-baseline">
                <h3>Organization</h3>
                <InfoTooltip
                  id="usage-sim-org-tooltip"
                  aria-label="About organization selection"
                  size="sm"
                  placement="top">
                  Simulating the subscription only applies to this selected
                  organization. To test banners, please go to the team page of
                  this organization. Applicable for non-personal subscription.
                </InfoTooltip>
              </div>
              <SelectField
                aria-label="Organization"
                items={orgOptions}
                value={orgID ?? null}
                isDisabled={subscriptionType === 'free_personal'}
                onChange={key => {
                  if (key != null) {
                    setOrgID(String(key));
                  }
                }}
              />
            </div>
            <div className="flex flex-col">
              <h3>Simulate the usage</h3>
              {(
                ['Storage GB', 'Tracked GB', 'Tracked hours'] as LimitType[]
              ).map(limitType => {
                const attr = getLimitInputAttributes(limitType);
                if (attr == null) {
                  return null;
                }
                return (
                  <LimitComponent
                    key={limitType}
                    type={limitType}
                    attr={attr}
                    planInfo={planInfo}
                  />
                );
              })}
              <div className="flex items-center justify-between py-2 pl-24 pr-8">
                <div className="flex items-baseline">
                  Days until expiration
                  <InfoTooltip
                    id="usage-sim-days-until-expire-tooltip"
                    aria-label="About days until expiration"
                    size="sm"
                    placement="top">
                    Setting a number (x) greater than 0 would simulate expiring
                    trial subscription with x days remaining. If you set 0, you
                    can see the banners for expired trial subscription.
                    Applicable only for trial subscription types.
                  </InfoTooltip>
                </div>
                <NumberField
                  aria-label="Days until expiration"
                  compact
                  value={daysUntilExpire ?? NaN}
                  onChange={value => {
                    setDaysUntilExpire(Number.isNaN(value) ? 0 : value);
                  }}
                />
              </div>
              <div className="flex items-center justify-between py-6 pl-24 pr-8">
                <div className="flex items-baseline">
                  <Switch
                    compact
                    isSelected={
                      subscriptionStatus ===
                      OrganizationSubscriptionStatus.Disabled
                    }
                    onChange={checked => {
                      setSubscriptionStatus(
                        checked
                          ? OrganizationSubscriptionStatus.Disabled
                          : OrganizationSubscriptionStatus.Enabled
                      );
                    }}>
                    Disabled
                  </Switch>
                  <InfoTooltip
                    id="usage-sim-disabled-tooltip"
                    aria-label="About the disabled toggle"
                    size="sm"
                    placement="top">
                    This can simulate a situation where their subscription is
                    disabled (deactivated).
                  </InfoTooltip>
                </div>
              </div>
              <div className="flex items-center justify-between py-6 pl-24 pr-8">
                <div className="flex items-baseline">
                  <Switch
                    compact
                    isSelected={aggressive}
                    // aggressive attribute is only relevant to
                    // compute hour nudge bar
                    isDisabled={
                      computeHourLimit != null && computeHourLimit <= 250
                    }
                    onChange={checked => {
                      setAggressive(checked);
                    }}>
                    Aggressive
                  </Switch>
                  <InfoTooltip
                    id="usage-sim-aggressive-tooltip"
                    aria-label="About the aggressive toggle"
                    size="sm"
                    placement="top">
                    {`This can simulate a situation where a user has seen the compute hour banner for ${COMPUTE_HOUR_AGGRESSIVE_DAYS_THRESHOLD}+ days, but haven't upgraded the subscription. Applicable only when you set compute hour greater than ${getLimitString(
                      250,
                      'hrs'
                    )}.`}
                  </InfoTooltip>
                </div>
              </div>
            </div>
          </TailwindContents>
        </Dialog.Content>
        <Dialog.Footer>
          <Button variant="destructive" onPress={onReset}>
            Reset workspace
          </Button>
          <Button variant="secondary" onPress={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onPress={onSubmit}>
            Apply
          </Button>
        </Dialog.Footer>
      </Dialog.Modal>
    );
  }
);

interface LimitComponentProps {
  type: LimitType;
  attr: LimitInputAttributes;
  planInfo: PlanInfoWithActualPlans | null;
}

const LimitComponent: React.FC<LimitComponentProps> = ({
  type,
  attr,
  planInfo,
}) => {
  const getLimitStringForPlan = useCallback(
    (plan?: PlanWithPrivileges) => {
      return getLimitString(
        plan?.defaultPrivileges[attr.privilegeLimitName] as number | undefined,
        attr.unit
      );
    },
    [attr]
  );

  return (
    <div
      key={type}
      className="flex items-center justify-between py-2 pl-24 pr-8">
      <div className="flex items-baseline">
        {type}
        <InfoTooltip
          id={`usage-sim-${type.toLowerCase().replace(/\s+/g, '-')}-tooltip`}
          aria-label={`About the ${type} limit`}
          size="sm"
          placement="top">
          {`${type} limit is ${getLimitStringForPlan(
            planInfo?.enterprisePlan
          )} for academic / enterprise plan.`}
        </InfoTooltip>
      </div>
      <NumberField
        aria-label={type}
        compact
        value={attr.limit ?? NaN}
        onChange={value => {
          attr.setLimit(Number.isNaN(value) ? 0 : value);
        }}
      />
    </div>
  );
};

export default UsageSimulationModal;
